import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { MessageSquare, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SEOHead, breadcrumbSchema } from '../components/seo/SEOHead';
import { downloadQRCodePNG, downloadQRCodeSVG } from '../utils/qrHelpers';
import { trackEvent } from '../components/analytics/GoogleAnalytics';

export const SMSQRPage: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [size, setSize] = useState(300);

  const qrContent = `SMSTO:${phone}:${message}`;
  const isValid = phone.length > 0;

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: 'https://freeqrcodenow.com/' },
    { name: 'SMS QR Code Generator', url: 'https://freeqrcodenow.com/sms-qr-code-generator' },
  ]);

  return (
    <>
      <SEOHead
        title="Free SMS QR Code Generator | Create SMS QR Codes Instantly"
        description="Generate free SMS QR codes for instant text messaging. Create QR codes that pre-fill phone numbers and messages. Download as PNG or SVG."
        keywords="sms qr code, text message qr, sms qr generator, free sms qr code, message qr code"
        structuredData={breadcrumbs}
      />

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            <MessageSquare className="inline h-10 w-10 mr-3 text-blue-600" />
            Free SMS QR Code Generator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create QR codes for instant SMS messaging. Pre-fill phone numbers and messages for easy communication!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle>SMS Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label="Phone Number *"
                placeholder="+1234567890"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                type="tel"
              />

              <div>
                <label className="block text-sm font-medium mb-2">Message (Optional)</label>
                <textarea
                  placeholder="Pre-fill message text here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full p-2 border border-input rounded-md bg-background min-h-[100px]"
                  maxLength={160}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {message.length}/160 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">QR Code Size: {size}px</label>
                <input
                  type="range"
                  min="200"
                  max="500"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card>
            <CardHeader>
              <CardTitle>QR Code Preview</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-6">
              {isValid ? (
                <>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <QRCodeSVG
                      value={qrContent}
                      size={size}
                      level="M"
                      includeMargin={true}
                    />
                  </div>

                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button onClick={() => {
                      downloadQRCodePNG(qrContent, { size, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, false, 'sms-qr-code.png');
                      trackEvent('download', 'SMS QR', 'PNG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button variant="outline" onClick={() => {
                      downloadQRCodeSVG(qrContent, { size, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, 'sms-qr-code.svg');
                      trackEvent('download', 'SMS QR', 'SVG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download SVG
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <MessageSquare className="h-16 w-16 mb-4 text-gray-300" />
                  <p>Enter phone number to generate QR code</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SEO Content */}
        <div className="mt-12 prose prose-lg max-w-none">
          <h2>How to Create an SMS QR Code</h2>
          <p>
            Creating an SMS QR code is quick and free with our generator:
          </p>
          <ol>
            <li>Enter the recipient's phone number (include country code)</li>
            <li>Optionally add a pre-filled message</li>
            <li>Adjust the QR code size as needed</li>
            <li>Download your QR code as PNG or SVG</li>
          </ol>

          <h2>Use Cases for SMS QR Codes</h2>
          <p>
            SMS QR codes are perfect for various marketing and communication scenarios:
          </p>
          <ul>
            <li><strong>Customer Support:</strong> Let customers quickly text for help</li>
            <li><strong>Marketing Campaigns:</strong> Enable easy opt-in for promotions</li>
            <li><strong>Feedback Collection:</strong> Simplify survey participation</li>
            <li><strong>Event Registration:</strong> Quick RSVP via text message</li>
            <li><strong>Contests & Giveaways:</strong> Easy entry via SMS</li>
            <li><strong>Emergency Contacts:</strong> Quick access to important numbers</li>
          </ul>

          <h2>Best Practices</h2>
          <ul>
            <li>Always include country code in phone numbers (+1 for US)</li>
            <li>Keep pre-filled messages short and clear</li>
            <li>Test your QR code before printing</li>
            <li>Ensure QR codes are large enough to scan easily</li>
            <li>Consider SMS regulations and opt-in requirements</li>
          </ul>

          <h2>Download Formats</h2>
          <p>
            We offer two download formats for maximum flexibility:
          </p>
          <ul>
            <li><strong>PNG:</strong> Perfect for digital use and printing</li>
            <li><strong>SVG:</strong> Vector format that scales without quality loss</li>
          </ul>
        </div>
      </div>
    </>
  );
};


